import { useEffect, useState } from 'react';
import { Key, Tab, TabList, TabPanel, Tabs } from 'react-aria-components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { useAtom } from 'jotai';
import * as yup from 'yup';

import { getAttendanceReservationByID } from '@/api/getAttendanceReservations';
import {
  SupportRecordInput,
  createSupportRecord,
  getSupportRecordByReservationID,
  updateSupportRecord,
} from '@/api/getSupportRecord';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import { SupportAndTreatmentForm } from '@/components/reservations/supportRecord/SupportAndTreatmentForm';
import { UsageStatusForm } from '@/components/reservations/supportRecord/UsageStatusForm';
import { VitalForm } from '@/components/reservations/supportRecord/VitalForm';
import { NotificationAtom, UserAtom } from '@/jotai/jotai';
import { fileToBase64 } from '@/logics/base64/base64';
import { formatDate } from '@/logics/date/date';
import { DetailedAttendanceReservation } from '@/types/attendanceReservation';
import { SupportRecord } from '@/types/supportRecord';

export type SupportRecordFormData = {
  attendanceStatus: 'absent' | 'attend';
  startTime?: string;
  endTime?: string;
  absentDate?: Date;
  absentReasonByGuardian?: string;
  absentReasonByStaff?: string;
  isRescheduled?: boolean;
  rescheduledDate?: Date;
  isAbsentAddition?: boolean;
  pickupOrArrivalTime?: string;
  isPickupAddition?: boolean;
  dropoffOrDepartureTime?: string;
  isDropoffAddition?: boolean;
  bodyTemperature?: number;
  condition?: string;
  supportContent?: string;
  staffMessage?: string;
  staffReport?: string;
  supportRecordImages?: string[];
};

export const SupportRecordPage = () => {
  const { pathname: pathName } = useLocation();
  const navigate = useNavigate();
  const breadcrumbs: BreadcrumbItem[] = [
    { path: 'admin/reservations/', name: '来所予約一覧' },
    { path: pathName, name: '来所予約詳細' },
  ];
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const [user] = useAtom(UserAtom);
  const [_, setNotification] = useAtom(NotificationAtom);

  const [supportRecord, setSupportRecord] = useState<SupportRecord | undefined>(undefined);
  const [reservation, setReservation] = useState<DetailedAttendanceReservation | undefined>(
    undefined
  );
  const [tabKey, setTabKey] = useState<Key>(1);
  const [supportRecordImageFiles, setSupportRecordImageFiles] = useState<File[]>([]);

  const schema: yup.ObjectSchema<SupportRecordFormData> = yup.object({
    attendanceStatus: yup.string().oneOf(['absent', 'attend']).required(),
    startTime: yup.string().when('attendanceStatus', {
      is: 'attend',
      then: (schema) => schema.required('開始時間は必須です'),
      otherwise: (schema) => schema.optional(),
    }),
    endTime: yup.string().when('attendanceStatus', {
      is: 'attend',
      then: (schema) => schema.required('終了時間は必須です'),
      otherwise: (schema) => schema.optional(),
    }),
    absentDate: yup.date().when('attendanceStatus', {
      is: 'absent',
      then: (schema) => schema.required('欠席日は必須です'),
      otherwise: (schema) => schema.optional(),
    }),
    absentReasonByGuardian: yup.string().when('attendanceStatus', {
      is: 'absent',
      then: (schema) => schema.required('保護者からの欠席理由は必須です'),
      otherwise: (schema) => schema.optional(),
    }),
    absentReasonByStaff: yup.string().when('attendanceStatus', {
      is: 'absent',
      then: (schema) => schema.required('事業所からの欠席理由は必須です'),
      otherwise: (schema) => schema.optional(),
    }),
    isRescheduled: yup.boolean().optional(),
    rescheduledDate: yup
      .date()
      .when(['attendanceStatus', 'isRescheduled'], ([attendanceStatus, isRescheduled]) => {
        return attendanceStatus === 'absent' && isRescheduled === true
          ? yup.date().required('振替日は必須です')
          : yup.date().optional();
      }),
    isAbsentAddition: yup.boolean().optional(),
    pickupOrArrivalTime: yup.string().when('attendanceStatus', {
      is: 'attend',
      then: (schema) => schema.required('送迎/到着時間は必須です'),
      otherwise: (schema) => schema.optional(),
    }),
    isPickupAddition: yup.boolean().optional(),
    dropoffOrDepartureTime: yup.string().when('attendanceStatus', {
      is: 'attend',
      then: (schema) => schema.required('送り/出発時間は必須です'),
      otherwise: (schema) => schema.optional(),
    }),
    isDropoffAddition: yup.boolean().optional(),
    bodyTemperature: yup.number().when('attendanceStatus', {
      is: 'attend',
      then: (schema) => schema.required('体温は必須です'),
      otherwise: (schema) => schema.optional(),
    }),
    condition: yup.string().when('attendanceStatus', {
      is: 'attend',
      then: (schema) => schema.required('体調は必須です'),
      otherwise: (schema) => schema.optional(),
    }),
    supportContent: yup.string().when('attendanceStatus', {
      is: 'attend',
      then: (schema) => schema.required('支援内容は必須です'),
      otherwise: (schema) => schema.optional(),
    }),
    staffMessage: yup.string().optional(),
    staffReport: yup.string().optional(),
    supportRecordImages: yup.array().optional(),
  });

  const {
    register,
    setValue,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<SupportRecordFormData>({
    defaultValues: supportRecord
      ? editSupportRecordFormData(supportRecord)
      : reservation
        ? createSupportRecordFormData(reservation)
        : undefined,
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  useEffect(() => {
    const fetchSupportRecord = async () => {
      const result = await getSupportRecordByReservationID(id);
      if (result.status === 200) {
        setSupportRecord(result.result);
        reset(editSupportRecordFormData(result.result));
      } else {
        if (result.status === 404) {
          await fetchReservation();
        } else {
          // エラー処理
          console.log();
        }
      }
    };
    const fetchReservation = async () => {
      const result = await getAttendanceReservationByID(id);
      if (result.status === 200) {
        setReservation(result.result);
        reset(createSupportRecordFormData(result.result));
      }
    };
    fetchSupportRecord();
    fetchReservation();
  }, [id]);

  const onSubmit: SubmitHandler<SupportRecordFormData> = async (data) => {
    // TODO: メールアドレスがstateから取得できないのでとりあえずlocalstorageから取得する
    const userEmail = localStorage.getItem('email');
    if (!userEmail) {
      console.log('メールアドレスが取得できません');
      return;
    }
    const input: SupportRecordInput = {
      recorder_email: userEmail,
      attendance_status: data.attendanceStatus,
    };

    // 欠席時は入力しないため色々省く
    if (data.attendanceStatus === 'absent') {
      input.absent_date = data.absentDate?.toISOString() ?? '';
      input.absent_reason_by_guardian = data.absentReasonByGuardian ?? '';
      input.absent_reason_by_staff = data.absentReasonByStaff ?? '';
      input.is_rescheduled = data.isRescheduled ?? false;
      input.is_absent_addition = data.isAbsentAddition ?? false;
      if (data.isRescheduled) {
        input.rescheduled_date = formatDate(data.rescheduledDate ?? new Date());
      }
    } else if (data.attendanceStatus === 'attend') {
      // 出席時は正しく開始時間と終了時間入力されているはず
      input.start_time = data.startTime ?? reservation?.start_time ?? '';
      input.end_time = data.endTime ?? reservation?.end_time ?? '';

      input.pickup_or_arrival_time = data.pickupOrArrivalTime ?? '';
      input.is_pickup_addition = data.isPickupAddition ?? false;
      input.dropoff_or_departure_time = data.dropoffOrDepartureTime ?? '';
      input.is_dropoff_addition = data.isDropoffAddition ?? false;
      input.body_temperature = data.bodyTemperature ?? 0;
      input.condition = data.condition ?? '';
      input.support_content = data.supportContent ?? '';
      input.staff_message = data.staffMessage ?? '';
      input.staff_report = data.staffReport ?? '';
      input.support_record_images = supportRecordImageFiles
        ? await Promise.all(supportRecordImageFiles.map((file) => fileToBase64(file)))
        : [];
    }

    if (!supportRecord) {
      // 新規作成
      try {
        const result = await createSupportRecord(id, input);
        if (result.status === 201) {
          setNotification({
            headingLevel: 'h2',
            type: 'success',
            bannerStyle: 'standard',
            title: '支援記録を作成しました',
            message: '支援記録を作成しました',
          });
          console.log(result.result);
        } else {
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '支援記録の作成に失敗しました',
            message: '支援記録の作成に失敗しました',
          });
          console.log('error');
        }
      } catch (error) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '支援記録の作成に失敗しました',
          message: '支援記録の作成に失敗しました',
        });
      }
    } else {
      // 更新
      try {
        const result = await updateSupportRecord(id, input);
        if (result.status === 200) {
          setNotification({
            headingLevel: 'h2',
            type: 'success',
            bannerStyle: 'standard',
            title: '支援記録を更新しました',
            message: '支援記録を更新しました',
          });
          console.log(result.result);
        } else {
          console.log('更新に失敗');
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '支援記録の更新に失敗しました',
            message: '支援記録の更新に失敗しました',
          });
          console.log('error');
        }
      } catch (error) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '支援記録の更新に失敗しました',
          message: '支援記録の更新に失敗しました',
        });
      }
    }
  };

  const handlePerformanceRecordClick = () => {
    if (reservation?.child.id) {
      navigate(`/admin/reservations/children/${reservation.child.id}/performances`);
    }
  };

  return (
    <div className="flex flex-col space-y-10">
      <div className="flex flex-row">
        <Breadcrumbs item={breadcrumbs} />
      </div>
      <div className="space-y-2">
        <div className="text-xl font-bold underline">
          {reservation?.child.last_name + ' ' + reservation?.child.first_name}
        </div>
        <div className="text-lg font-bold">
          記録担当者:
          {supportRecord ? supportRecord.recorder.name : user?.last_name + ' ' + user?.first_name}
        </div>
      </div>
      <div className="space-x-4">
        <Button
          size="lg"
          type="submit"
          variant="solid-fill"
        >
          支援記録表
        </Button>
        <Button
          size="lg"
          type="submit"
          variant="solid-fill"
        >
          支援計画
        </Button>
        <Button
          size="lg"
          type="submit"
          variant="solid-fill"
          onClick={handlePerformanceRecordClick}
        >
          実績記録表
        </Button>
      </div>
      <div className="flex w-[1000px] flex-col">
        <Tabs
          defaultSelectedKey="usage-status"
          selectedKey={tabKey}
          onSelectionChange={setTabKey}
          className="w-full"
        >
          <TabList
            aria-label="support-record"
            className="mb-4 flex border-b border-gray-200"
          >
            <Tab
              id="usage-status"
              className="px-8 py-2 text-gray-600 focus:outline-none data-[selected=false]:border-transparent data-[selected=true]:border-blue-900 data-[selected=true]:text-blue-900"
            >
              利用状況
            </Tab>
            <Tab
              id="vital"
              className="px-8 py-2 text-gray-400 focus:outline-none data-[selected=false]:border-transparent data-[selected=true]:border-blue-900 data-[selected=true]:text-blue-900"
            >
              バイタル
            </Tab>
            <Tab
              id="support-and-treatment"
              className="px-8 py-2 text-gray-400 focus:outline-none data-[selected=false]:border-transparent data-[selected=true]:border-blue-900 data-[selected=true]:text-blue-900"
            >
              支援・療育記録
            </Tab>
          </TabList>
          <div className="flex w-[1000px] flex-col">
            <TabPanel
              id="usage-status"
              className="p-4"
            >
              <UsageStatusForm
                reservation={reservation}
                supportRecord={supportRecord}
                register={register}
                control={control}
                watch={watch}
                errors={errors}
              />
            </TabPanel>
            <TabPanel
              id="vital"
              className="p-4"
            >
              <VitalForm
                register={register}
                setValue={setValue}
                errors={errors}
              />
            </TabPanel>
            <TabPanel
              id="support-and-treatment"
              className="p-4"
            >
              <SupportAndTreatmentForm
                register={register}
                watch={watch}
                setValue={setValue}
                errors={errors}
                supportRecord={supportRecord}
                setSupportRecordImageFiles={setSupportRecordImageFiles}
              />
            </TabPanel>
          </div>
        </Tabs>
        <div className="my-4 flex justify-center space-x-4">
          <Button
            size="lg"
            variant="outline"
            onClick={() => console.log('clicked')}
          >
            戻る
          </Button>
          <Button
            size="lg"
            variant="solid-fill"
            onClick={handleSubmit(onSubmit)}
          >
            保存
          </Button>
        </div>
      </div>
    </div>
  );
};

function createSupportRecordFormData(
  reservation: DetailedAttendanceReservation
): SupportRecordFormData {
  const formData: SupportRecordFormData = {
    attendanceStatus: reservation.attendance_status ?? 'attend',
    startTime: reservation.start_time,
    endTime: reservation.end_time,
    supportRecordImages: [],
  };
  // 出席する場合
  if (reservation.attendance_status === 'attend') {
    // 迎えが必要かどうかで分岐
    if (reservation.pickup_info?.isRequired) {
      formData.pickupOrArrivalTime = reservation.pickup_info.pickup_time;
    } else {
      formData.pickupOrArrivalTime = reservation.pickup_info?.arrival_time;
    }
    // 送りが必要かどうかで分岐
    if (reservation.dropoff_info?.isRequired) {
      formData.dropoffOrDepartureTime = reservation.dropoff_info.dropoff_time;
    } else {
      formData.dropoffOrDepartureTime = reservation.dropoff_info?.departure_time;
    }
  } else {
    // 欠席する場合
    formData.absentDate = reservation.absent_date ? new Date(reservation.absent_date) : undefined;
    formData.absentReasonByGuardian = reservation.absent_reason ?? '';
  }

  return formData;
}

function editSupportRecordFormData(supportRecord: SupportRecord): SupportRecordFormData {
  return {
    attendanceStatus: supportRecord.attendance_status,
    startTime: supportRecord.start_time,
    endTime: supportRecord.end_time,
    absentDate: supportRecord.absent_date ? new Date(supportRecord.absent_date) : undefined,
    absentReasonByGuardian: supportRecord.absent_reason_by_guardian,
    absentReasonByStaff: supportRecord.absent_reason_by_staff,
    isRescheduled: supportRecord.is_rescheduled,
    rescheduledDate: supportRecord.rescheduled_date
      ? new Date(supportRecord.rescheduled_date)
      : undefined,
    isAbsentAddition: supportRecord.is_absent_addition,
    pickupOrArrivalTime: supportRecord.pickup_or_arrival_time,
    isPickupAddition: supportRecord.is_pickup_addition,
    dropoffOrDepartureTime: supportRecord.dropoff_or_departure_time,
    isDropoffAddition: supportRecord.is_dropoff_addition,
    bodyTemperature: supportRecord.body_temperature,
    condition: supportRecord.condition,
    supportContent: supportRecord.support_content,
    staffMessage: supportRecord.staff_message,
    staffReport: supportRecord.staff_report,
    supportRecordImages: supportRecord.support_record_images
      ? supportRecord.support_record_images.map((image) => image)
      : [],
  };
}
